import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import {
  getPlanCalendar,
  assignPlanCalendar,
  detachPlanCalendar,
} from '@/api/calendars'
import type { PlanCalendarResponse } from '@/types'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { useCurrentVersionStore } from '@/stores/useCurrentVersionStore'

function invalidateDagQueries(queryClient: QueryClient, planId: string) {
  const versionId =
    useCurrentVersionStore.getState().versionsByPlan[planId] ??
    queryClient.getQueryData<string>(queryKeys.planActiveVersion.byId(planId))
  if (versionId) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.dagTable.metadata(planId, versionId),
    })
  }
}

export function usePlanCalendar(planId: string, isSeasonal: boolean = false) {
  return useQuery<PlanCalendarResponse | null>({
    queryKey: queryKeys.planDetail.calendar(planId, isSeasonal),
    queryFn: async () => {
      try {
        return await getPlanCalendar(planId, isSeasonal)
      } catch (error: unknown) {
        // 404 means no calendar assigned – not an error
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { status?: number } }).response?.status === 404
        ) {
          return null
        }
        throw error
      }
    },
    enabled: !!planId,
  })
}

export function useAssignPlanCalendar(
  planId: string,
  isSeasonal: boolean = false
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (calendarEventId: string) =>
      assignPlanCalendar(planId, calendarEventId, isSeasonal),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.calendar(planId, isSeasonal),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.browse.all,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.planUndoStack.byPlanId(planId),
      })
      if (isSeasonal) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        invalidateDagQueries(queryClient, planId)
      }
      toast.success(
        isSeasonal
          ? 'Seasonal calendar event assigned successfully'
          : 'Calendar event assigned successfully'
      )
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to assign calendar event'))
    },
  })
}

export function useDetachPlanCalendar(
  planId: string,
  isSeasonal: boolean = false
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => detachPlanCalendar(planId, isSeasonal),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.calendar(planId, isSeasonal),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.browse.all,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.planUndoStack.byPlanId(planId),
      })
      if (isSeasonal) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        invalidateDagQueries(queryClient, planId)
      }
      toast.success(
        isSeasonal
          ? 'Seasonal calendar removed successfully'
          : 'Calendar removed successfully'
      )
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to remove calendar'))
    },
  })
}
